//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import Cuid from '@models/Cuid';
import { RejectionCode } from '@models/RejectionCode';

export interface RejectionReasonDto {
  description: string;
  rejectionReasonId: number;
}

export interface RejectionReasonsResponse {
  data: RejectionReasonDto[];
}

export interface RejectModerationMediaRequest {
  cuids: Cuid[];
  rejectionReasonCode: RejectionCode;
}

export interface ShareModerationMediaRequest {
  cuids: Cuid[];
  isForceReviewed: boolean;
}

export type GuidelinesConfigType = 'GeneralImageFilter' | 'GeneralVideoFilter';

export interface StaticRejectionReasons {
  contentAndPolicyReasons: string[];
  safetyReasons: string[];
}

export interface ContentCurationGuidelines {
  brandAccountContentEvaluation: 'ApproveAll' | 'RejectAll';
  brandName: string;
  brandType: 'SingleBrand' | 'RetailerAggregator';
  competitorList: string[];
  configType: GuidelinesConfigType;
  createdAt: number;
  hashtagsToReject: string[];
  minorPolicy:
    | 'AllowIfSafe'
    | 'RejectUnder13ChildrenAndBabies'
    | 'RejectUnder18ChildrenAndBabies';
  preferredStyles: string[];
  preferredVisualQualities: ('Stunning' | 'VeryGood' | 'Fair')[];
  productCategories: string[];
  productVisibilityPolicy?: 'ProductVisibilityRequired' | 'AllowProductOutput';
  rejectedStyles: string[];
  shouldAllowCompetitors: boolean;
  shouldAllowInStoreMedia: boolean;
  shouldAllowSponsoredMedia: boolean;
  shouldAllowTextOverlayOnMedia: boolean;
  shouldAllowViceContent: boolean;
  staticRejectionReasons: StaticRejectionReasons;
  updatedAt: number;
}

export interface GetContentCurationGuidelinesResponse {
  data: ContentCurationGuidelines[];
}

export interface UpdateContentCurationGuidelinesRequest {
  brandAccountContentEvaluation: 'ApproveAll' | 'RejectAll';
  brandName: string;
  brandType: 'SingleBrand' | 'RetailerAggregator';
  competitorList?: string[];
  hashtagsToReject?: string[];
  minorPolicy:
    | 'AllowIfSafe'
    | 'RejectUnder13ChildrenAndBabies'
    | 'RejectUnder18ChildrenAndBabies';
  preferredStyles?: string[];
  preferredVisualQualities?: ('Stunning' | 'VeryGood' | 'Fair')[];
  productCategories?: string[];
  rejectedStyles?: string[];
  shouldAllowCompetitors: boolean;
  shouldAllowInStoreMedia: boolean;
  shouldAllowSponsoredMedia: boolean;
  shouldAllowTextOverlayOnMedia: boolean;
  shouldAllowViceContent: boolean;
  shouldRequireProductVisibility: boolean;
}

export interface BatchUpdateModerationStatusRequest {
  contents: ModerationStatusInfo[];
}

export interface ModerationStatusInfo {
  cuid: Cuid;
  status: ModerationStatus;
  statusSource: ModerationStatusSource;
}

export enum ModerationStatus {
  Approved = 'Approved',
  Rejected = 'Rejected',
}

export enum ModerationStatusSource {
  AiEvaluation = 'AiEvaluation',
  AutoApprovedBrandAccount = 'AutoApprovedBrandAccount',
  AutoRejectedBrandAccount = 'AutoRejectedBrandAccount',
  AutoRejectedHashtag = 'AutoRejectedHashtag',
  UserOverride = 'UserOverride',
}
